import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const WebsiteExperienceReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [userName, setUserName] = useState("");
  const [userReview, setUserReview] = useState("");
  const [userRating, setUserRating] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Global Engine Endpoint
  const API_ENDPOINT = "http://localhost:5000/api/reviews";

  // Fetch reviews from the backend pipeline
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(API_ENDPOINT);
        setReviews(response.data);
      } catch (error) {
        console.error("Error connecting to data engine:", error);
      }
    };
    fetchReviews();
  }, []);

  // Handle structural form submission
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!userName.trim() || !userReview.trim()) return;

    setIsSubmitting(true);
    const newReview = {
      name: userName.trim(),
      review: userReview.trim(),
      rating: userRating,
      date: new Date().toLocaleDateString(),
    };

    try {
      const response = await axios.post(API_ENDPOINT, newReview);

      // Inject entry cleanly into local layout matrix
      setReviews((prevReviews) => [...prevReviews, response.data]);

      // Reset application input tracking states smoothly
      setUserName("");
      setUserReview("");
      setUserRating(5);
    } catch (error) {
      console.error("Pipeline submission fault:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Framer Motion Animation Settings (Non-AI Look Configurations)
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } },
  };

  return (
    <section
      className="position-relative py-5 min-vh-100 d-flex flex-column justify-content-center"
      style={{
        background:
          "linear-gradient(135deg, var(--brand-charcoal) 0%, var(--brand-deep-blue) 100%)",
        color: "var(--brand-pure-white)",
      }}
    >
      {/* Decorative Branding Ambient Glows */}
      <div
        className="position-absolute rounded-circle opacity-25"
        style={{
          width: "400px",
          height: "400px",
          top: "-5%",
          right: "-10%",
          background: "var(--brand-mineral-aqua)",
          filter: "blur(130px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle opacity-25"
        style={{
          width: "350px",
          height: "350px",
          bottom: "5%",
          left: "-10%",
          background: "var(--brand-sky-blue)",
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />

      <div className="container position-relative z-1 py-4">
        {/* Core AEO Structured Context Header */}
        <div className="text-center mb-5">
          <span
            className="text-uppercase fw-bold px-3 py-1 rounded-pill small d-inline-block mb-3"
            style={{
              backgroundColor: "var(--brand-sky-blue)",
              color: "var(--brand-deep-blue)",
              fontSize: "0.75rem",
              letterSpacing: "1.2px",
            }}
          >
            User Analytics & Feedback
          </span>
          <h2 className="display-6 fw-bold text-white mb-2">
            Website Experience Metrics
          </h2>
          <p
            className="text-white-50 mx-auto"
            style={{ maxWidth: "600px", fontSize: "0.95rem" }}
          >
            Real-time verified evaluation indices submitted by open-source
            platform contributors and deployment engineers.
          </p>
        </div>

        {/* Display Reviews with Micro-Interactions */}
        <AnimatePresence mode="popLayout">
          {reviews.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center text-white-50 py-5 my-3 rounded-4 backdrop-blur"
              style={{
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px dashed rgba(255,255,255,0.1)",
              }}
            >
              <i className="bi bi-chat-left-heart display-6 mb-3 d-block text-muted"></i>
              <p className="mb-0">
                No active operational logs. Initiate the pipeline below by
                providing the first footprint.
              </p>
            </motion.div>
          ) : (
            <motion.div
              className="row g-4 mb-5"
              variants={containerVariants}
              initial="hidden"
              animate="show"
              layout
            >
              {reviews.map((review) => (
                <motion.div
                  key={review._id || `local_${review.date}_${review.name}`}
                  className="col-12 col-md-6 col-lg-4 d-flex"
                  variants={itemVariants}
                  layout
                  exit={{
                    opacity: 0,
                    scale: 0.9,
                    transition: { duration: 0.2 },
                  }}
                >
                  <article
                    className="card w-100 backdrop-blur rounded-4 p-4 border-1 d-flex flex-column justify-content-between"
                    style={{
                      background: "rgba(255, 255, 255, 0.05)",
                      borderColor: "rgba(255, 255, 255, 0.1)",
                      boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
                      transition: "var(--slomo-transition)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.borderColor =
                        "var(--brand-mineral-aqua)";
                      e.currentTarget.style.background =
                        "rgba(255, 255, 255, 0.09)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor =
                        "rgba(255, 255, 255, 0.1)";
                      e.currentTarget.style.background =
                        "rgba(255, 255, 255, 0.05)";
                    }}
                  >
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="h6 fw-bold text-white mb-0">
                          {review.name}
                        </h5>
                        <small
                          className="text-white-50"
                          style={{ fontSize: "0.75rem" }}
                        >
                          {review.date}
                        </small>
                      </div>
                      <p
                        className="text-white-50 fs-6 mb-4"
                        style={{ fontStyle: "italic", lineHeight: "1.6" }}
                      >
                        "{review.review}"
                      </p>
                    </div>
                    <div
                      className="border-top pt-3"
                      style={{ borderColor: "rgba(255,255,255,0.08)" }}
                    >
                      <div
                        className="d-flex gap-1"
                        aria-label={`Rated ${review.rating} stars`}
                      >
                        {Array.from({ length: 5 }, (_, i) => (
                          <i
                            key={i}
                            className={`bi ${
                              i < review.rating
                                ? "bi-star-fill text-warning"
                                : "bi-star text-white-20"
                            }`}
                            style={{ fontSize: "0.95rem" }}
                          />
                        ))}
                      </div>
                    </div>
                  </article>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Premium Form Workspace Container */}
        <motion.div
          className="card backdrop-blur rounded-4 border-1 p-4 p-md-5 mt-5 mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 80 }}
          style={{
            maxWidth: "700px",
            background: "rgba(255, 255, 255, 0.04)",
            borderColor: "rgba(255, 255, 255, 0.08)",
            boxShadow: "0 30px 60px rgba(0, 0, 0, 0.3)",
          }}
        >
          <h4 className="text-white fw-bold mb-4 text-center">
            Append System Experience Log
          </h4>
          <form onSubmit={handleSubmitReview}>
            <div className="row">
              <div className="col-12 col-md-8 mb-3">
                <label
                  htmlFor="userName"
                  className="form-label small fw-semibold text-white-50"
                >
                  Identity Name
                </label>
                <input
                  type="text"
                  className="form-control text-white border-0 py-2.5 px-3 rounded-3"
                  id="userName"
                  placeholder="e.g., Engineer / Contributor"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                  style={{
                    background: "rgba(255, 255, 255, 0.07)",
                    borderBottom: "2px solid rgba(255,255,255,0.15)",
                  }}
                />
              </div>

              <div className="col-12 col-md-4 mb-3">
                <label
                  htmlFor="userRating"
                  className="form-label small fw-semibold text-white-50"
                >
                  Rating Metric
                </label>
                <select
                  id="userRating"
                  className="form-select text-white border-0 py-2.5 px-3 rounded-3"
                  value={userRating}
                  onChange={(e) => setUserRating(Number(e.target.value))}
                  style={{
                    background: "rgba(255, 255, 255, 0.07)",
                    borderBottom: "2px solid rgba(255,255,255,0.15)",
                    cursor: "pointer",
                  }}
                >
                  <option value={5} className="bg-dark">
                    5 Stars Matrix
                  </option>
                  <option value={4} className="bg-dark">
                    4 Stars Balanced
                  </option>
                  <option value={3} className="bg-dark">
                    3 Stars Baseline
                  </option>
                  <option value={2} className="bg-dark">
                    2 Stars Fragmented
                  </option>
                  <option value={1} className="bg-dark">
                    1 Star Critical
                  </option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label
                htmlFor="userReview"
                className="form-label small fw-semibold text-white-50"
              >
                Detailed Narrative Statement
              </label>
              <textarea
                className="form-control text-white border-0 py-2.5 px-3 rounded-3"
                id="userReview"
                rows="4"
                placeholder="Share technical metrics or experience breakdowns regarding user workflows..."
                value={userReview}
                onChange={(e) => setUserReview(e.target.value)}
                required
                style={{
                  background: "rgba(255, 255, 255, 0.07)",
                  borderBottom: "2px solid rgba(255,255,255,0.15)",
                  resize: "none",
                }}
              ></textarea>
            </div>

            <div className="d-grid">
              <motion.button
                type="submit"
                className="btn fw-bold py-3 text-uppercase tracking-wider rounded-3 border-0"
                disabled={isSubmitting}
                whileHover={{
                  scale: 1.01,
                  backgroundColor: "var(--brand-mineral-aqua)",
                }}
                whileTap={{ scale: 0.99 }}
                style={{
                  background: "var(--brand-sky-blue)",
                  color: "var(--brand-deep-blue)",
                  letterSpacing: "1px",
                }}
              >
                {isSubmitting ? "Syncing Pipeline..." : "Commit Statement"}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default WebsiteExperienceReviews;
